import { Button } from "@/components/ui/button";
import { getCurrentUserToken } from "@/lib/auth";
import eventTrackingHandler from "@/tracking/eventTrackingHandler";
import { cn } from "@/utils/cn";
import { useState } from "react";
import { BASE_DASHB_EVENT_PROPS, DASHBOARD_EVENTS } from "../events.dashboard";
import { addPaymentMethod } from "../handlers/addPaymentMethod";
import { useGetUserBillingDetailQuery } from "../hooks/queries/useGetUserBillingDetailQuery";
import { usePaymentDetailQuery } from "../hooks/queries/usePaymentDetailQuery";
import { ComponentCard, ComponentHeader } from "../ui";

const PAYMENT_CONFIG = {
  paymentMethods: {
    title: "Payment Methods",
    addButton: "Add Payment Method",
    noPaymentMethods: "No payment methods added",
  },
  billingShipping: {
    title: "Billing and Shipping Address",
    addButton: "Add Addresses",
    noBillingShipping: "No billing and shipping addresses",
  },
} as const;

const formatCardBrand = (brand: string) =>
  brand.charAt(0).toUpperCase() + brand.slice(1);

function LoadingButton({
  onClick,
  isLoading,
  label,
}: {
  onClick: () => void;
  isLoading: boolean;
  label: string;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={isLoading}
      variant="outline"
      size="sm"
      className="grid place-content-center place-items-center [grid-template-areas:'stack']"
    >
      <div
        className={cn("[grid-area:stack]", isLoading ? "invisible" : "visible")}
      >
        {label}
      </div>

      <div
        className={cn("[grid-area:stack]", isLoading ? "visible" : "invisible")}
      >
        Loading...
      </div>
    </Button>
  );
}

function PaymentMethodSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-14.5 animate-pulse rounded-sm bg-gray-200" />
      <div className="flex flex-col gap-2">
        <div className="h-4 w-32 animate-pulse rounded-sm bg-gray-200" />
        <div className="h-3 w-24 animate-pulse rounded-sm bg-gray-200" />
      </div>
    </div>
  );
}

function BillingDetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {[16, 20, 16].map((labelWidth, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div
            className="h-4 animate-pulse rounded-sm bg-gray-200"
            style={{ width: `${labelWidth * 4}px` }}
          />
          <div className="h-5 w-48 animate-pulse rounded-sm bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

function BillingField({ label, value }: { label: string; value: string }) {
  if (!value.trim()) return null;
  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-archivo leading-7 font-normal text-[#323232] md:text-xl">
        {label}
      </h4>
      <p className="font-archivo text-base leading-relaxed font-normal text-[#323232]">
        {value}
      </p>
    </div>
  );
}

export default function PaymentAndBilling() {
  const { data: paymentMethods, isLoading } = usePaymentDetailQuery();
  const { data: billingDetail, isLoading: isBillingLoading } =
    useGetUserBillingDetailQuery();

  const [isLoadingPaymentPortal, setIsLoadingPaymentPortal] = useState(false);
  const [isLoadingBillingPortal, setIsLoadingBillingPortal] = useState(false);

  const cardPaymentMethods =
    paymentMethods?.filter((pm) => pm.type === "card") ?? [];

  async function openPortal(
    setLoading: (v: boolean) => void,
    trackEvent: () => void,
  ) {
    try {
      trackEvent();
      setLoading(true);
      const token = await getCurrentUserToken();
      if (!token) {
        console.error("No authentication token");
        return;
      }
      const portalUrl = await addPaymentMethod({ token });
      window.open(portalUrl, "_self");
    } catch (error) {
      console.error("Error opening portal:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleAddPaymentMethod() {
    openPortal(setIsLoadingPaymentPortal, () =>
      eventTrackingHandler(DASHBOARD_EVENTS.ADD_PAYMENT_METHOD, {
        ...BASE_DASHB_EVENT_PROPS,
        types: ["posthog", "customerio"],
        properties: {
          existing_payment_methods_count: cardPaymentMethods.length,
        },
      }),
    );
  }

  function handleAddBillingAddress() {
    openPortal(setIsLoadingBillingPortal, () =>
      eventTrackingHandler(DASHBOARD_EVENTS.ADD_BILLING_ADDRESS, {
        ...BASE_DASHB_EVENT_PROPS,
        types: ["posthog", "customerio"],
        properties: { has_existing_billing_detail: !!billingDetail },
      }),
    );
  }

  const formattedAddress = billingDetail?.billing_address
    ? [
        billingDetail.billing_address.line1,
        billingDetail.billing_address.line2,
        billingDetail.billing_address.city,
        billingDetail.billing_address.state,
        billingDetail.billing_address.country,
        billingDetail.billing_address.postal_code,
      ]
        .filter((part) => part?.trim())
        .join(", ")
    : "";

  return (
    <div id="payment-and-billing" className="">
      <div className="app-container space-y-6">
        <ComponentHeader>Payment and Billing</ComponentHeader>

        <ComponentCard>
          {/* Payment Methods */}
          <div className="flex flex-col gap-5 md:flex-row md:items-center">
            <div className="flex-1 space-y-3">
              <h3 className="font-archivo text-lg leading-7 font-normal text-[#323232] md:text-xl">
                {PAYMENT_CONFIG.paymentMethods.title}
              </h3>

              <div className="flex flex-col gap-3">
                {isLoading ? (
                  <PaymentMethodSkeleton />
                ) : cardPaymentMethods.length > 0 ? (
                  cardPaymentMethods.map((pm) => (
                    <div
                      key={pm.id}
                      className="flex items-center gap-3 rounded-lg"
                    >
                      <img
                        className="h-10 w-auto"
                        src={pm.image_url ?? "https://placehold.co/40x40"}
                        alt={`${pm.card?.brand} logo`}
                      />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-center gap-1.5">
                          <span className="font-archivo text-base leading-relaxed font-semibold tracking-wide text-[#323232]">
                            {formatCardBrand(pm.card?.brand ?? "")} ****{" "}
                            {pm.card?.last4}
                          </span>
                        </div>
                        <span className="font-archivo text-sm leading-relaxed font-light tracking-wide text-[#323232]">
                          Expires {pm.card?.expiry_month}/{pm.card?.expiry_year}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="font-archivo text-sm text-gray-500">
                    {PAYMENT_CONFIG.paymentMethods.noPaymentMethods}
                  </p>
                )}
              </div>
            </div>

            <LoadingButton
              onClick={handleAddPaymentMethod}
              isLoading={isLoadingPaymentPortal}
              label={PAYMENT_CONFIG.paymentMethods.addButton}
            />
          </div>

          <div className="border-t border-edge-light" />

          {/* Billing & Shipping */}
          <div className="flex flex-col gap-5 py-2 md:flex-row md:items-center">
            <div className="flex flex-1 flex-col gap-5">
              <h3 className="font-archivo text-lg leading-7 font-normal text-[#323232] md:text-xl">
                {PAYMENT_CONFIG.billingShipping.title}
              </h3>

              {isBillingLoading ? (
                <BillingDetailSkeleton />
              ) : billingDetail ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <BillingField
                    label="Email"
                    value={billingDetail.email ?? ""}
                  />
                  <BillingField label="Address" value={formattedAddress} />
                  <BillingField
                    label="Phone"
                    value={billingDetail.phone ?? ""}
                  />
                </div>
              ) : (
                <p className="font-archivo text-sm text-gray-500">
                  {PAYMENT_CONFIG.billingShipping.noBillingShipping}
                </p>
              )}
            </div>

            <LoadingButton
              onClick={handleAddBillingAddress}
              isLoading={isLoadingBillingPortal}
              label={PAYMENT_CONFIG.billingShipping.addButton}
            />
          </div>
        </ComponentCard>
      </div>
    </div>
  );
}
